/**
 * Versionsverlauf einer Mail: wer hat wann was geändert. Je Version
 * ansehen, mit der Vorversion oder der aktuellen Fassung vergleichen
 * (Block für Block, wortgenau) und — fürs Team — wiederherstellen.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Eye, History, RotateCcw } from 'lucide-react';
import { useToast } from '../Toast';
import { VERSION_QUELLE, datumZeit, istKonflikt, type PlanungsDetail, type VersionKopf, type VersionVoll, type WerkstattAdapter } from '../../lib/planung';
import { bloeckeVergleichen, wortDiff, type BlockUnterschied } from '../../lib/zusammenarbeit';
import { DiffText, Rueckfrage, Zeit } from './Teile';
import { KONFLIKT_TEXT, diffKuerzen, fehlerText, istVersendet } from './werkstattHilfen';

type Props = {
  mail: PlanungsDetail;
  adapter: WerkstattAdapter;
  /** Version, die gerade auf der Bühne steht (null = aktuelle Fassung) */
  gezeigtNr: number | null;
  onAnsehen: (v: VersionVoll | null) => void;
  /** Ungespeicherte Textänderungen in der Werkstatt — gehen beim Wiederherstellen verloren */
  ungespeichert?: boolean;
  /** Bearbeiten verlassen (Änderungen verwerfen) */
  onVerwerfen?: () => void;
};

type Vergleich = 'vorher' | 'jetzt';

/** Was nach dem Wiederherstellen in Brevo passiert (die Team-Seite überträgt Entwürfe selbst). */
function brevoSatz(m: PlanungsDetail): string | null {
  if (!m.brevo_id) return null;
  if (m.brevo_status === 'draft') return 'Der Brevo-Entwurf wird gleich mit aktualisiert.';
  if (m.brevo_status === 'suspended') return 'In Brevo ändert sich erst etwas, wenn Du die Mail dorthin überträgst.';
  if (!m.brevo_status) return 'Ist die Mail in Brevo noch ein Entwurf, wird er gleich mit aktualisiert.';
  return 'In Brevo ist die Mail schon eingeplant — dort ändert sich dadurch nichts.';
}

const SICHTBAR = 12;

export function VersionenPanel({ mail, adapter, gezeigtNr, onAnsehen, ungespeichert, onVerwerfen }: Props) {
  const versionen = mail.versionen;
  const [gewaehlt, setGewaehlt] = useState<number | null>(null);

  if (versionen.length === 0) {
    return (
      <div className="za-panel">
        <div className="za-leer">
          <History size={22} aria-hidden="true" />
          <p className="za-leer-titel">Noch keine Versionen</p>
          <p className="za-leer-text">Sobald jemand den Inhalt ändert, steht hier, wer was wann geändert hat.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="za-panel">
      <p className="za-panel-text">
        Jede Änderung am Inhalt wird als Version festgehalten. Wähle eine Version, um sie anzusehen oder zu vergleichen.
      </p>
      <ol className="za-versionen" aria-label="Versionen, neueste zuerst">
        {versionen.map((v, i) => {
          const offen = gewaehlt === v.nr;
          const aktuell = v.nr === mail.version_nr;
          const spaeter = v.updated_at && Math.abs(new Date(v.updated_at).getTime() - new Date(v.created_at).getTime()) > 60_000;
          return (
            <li key={v.nr} className={`za-version${offen ? ' za-version--offen' : ''}${aktuell ? ' za-version--aktuell' : ''}`}>
              <button
                type="button"
                className="za-version-knopf"
                aria-expanded={offen}
                onClick={() => setGewaehlt(offen ? null : v.nr)}
              >
                <span className="za-version-punkt" aria-hidden="true" />
                <span className="za-version-inhalt">
                  <span className="za-version-zeile">
                    <span className="za-version-nr">Version {v.nr}</span>
                    {aktuell && <span className="badge badge-success">aktuell</span>}
                    {gezeigtNr === v.nr && <span className="badge badge-info">wird angezeigt</span>}
                  </span>
                  <span className="za-version-meta">
                    {VERSION_QUELLE[v.quelle]}{v.autor_name && v.autor_name !== VERSION_QUELLE[v.quelle] ? ` · ${v.autor_name}` : ''}
                  </span>
                  <span className="za-version-meta">
                    <Zeit iso={v.created_at} />
                    {spaeter && <> · zuletzt <Zeit iso={v.updated_at} /></>}
                  </span>
                  {v.notiz && <span className="za-version-notiz">{v.notiz}</span>}
                </span>
                <ChevronDown size={16} aria-hidden="true" className="za-version-pfeil" />
              </button>
              {offen && (
                <VersionDetails
                  kopf={v}
                  vorgaenger={versionen[i + 1] ?? null}
                  mail={mail}
                  adapter={adapter}
                  gezeigt={gezeigtNr === v.nr}
                  onAnsehen={onAnsehen}
                  onFertig={() => setGewaehlt(null)}
                  ungespeichert={!!ungespeichert}
                  onVerwerfen={onVerwerfen}
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

type DetailProps = {
  kopf: VersionKopf;
  vorgaenger: VersionKopf | null;
  mail: PlanungsDetail;
  adapter: WerkstattAdapter;
  gezeigt: boolean;
  onAnsehen: (v: VersionVoll | null) => void;
  onFertig: () => void;
  ungespeichert: boolean;
  onVerwerfen?: () => void;
};

function VersionDetails({ kopf, vorgaenger, mail, adapter, gezeigt, onAnsehen, onFertig, ungespeichert, onVerwerfen }: DetailProps) {
  const toast = useToast();
  const aktuell = kopf.nr === mail.version_nr;
  const [wahl, setWahl] = useState<Vergleich>('vorher');
  const [fragen, setFragen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const vergleich: Vergleich = aktuell || !vorgaenger ? (vorgaenger ? 'vorher' : 'jetzt') : wahl;

  const diese = useQuery({
    queryKey: ['werkstatt-version', adapter.art, mail.id, kopf.nr, kopf.updated_at],
    queryFn: () => adapter.version(kopf.nr),
    enabled: !aktuell,
    staleTime: Infinity,
  });
  const vor = useQuery({
    queryKey: ['werkstatt-version', adapter.art, mail.id, vorgaenger?.nr ?? 0, vorgaenger?.updated_at ?? ''],
    queryFn: () => adapter.version(vorgaenger!.nr),
    enabled: !!vorgaenger && vergleich === 'vorher',
    staleTime: Infinity,
  });

  const dieseHtml = aktuell ? mail.html : diese.data?.html ?? null;
  const [htmlA, htmlB, betreffA, betreffB] = vergleich === 'vorher'
    ? [vor.data?.html ?? null, dieseHtml, vor.data?.betreff ?? vorgaenger?.betreff ?? null, aktuell ? mail.betreff : kopf.betreff]
    : [dieseHtml, mail.html, kopf.betreff, mail.betreff];
  const laedt = (!aktuell && diese.isLoading) || (vergleich === 'vorher' && vor.isLoading);
  const fehler = (!aktuell && diese.error) || (vergleich === 'vorher' && vor.error);
  const gleichWieJetzt = !aktuell && kopf.md5 === mail.md5;
  const darfWiederherstellen = adapter.darfEntscheiden && !!adapter.wiederherstellen && !aktuell && !gleichWieJetzt && !istVersendet(mail);

  const unterschiede = useMemo(
    () => (htmlA !== null && htmlB !== null ? bloeckeVergleichen(htmlA, htmlB) : null),
    [htmlA, htmlB],
  );

  async function wiederherstellen() {
    if (!adapter.wiederherstellen) return;
    setLaeuft(true);
    try {
      if (ungespeichert) onVerwerfen?.();
      await adapter.wiederherstellen(kopf.nr, mail.md5);
      toast.success(`Version ${kopf.nr} wiederhergestellt.`);
      setFragen(false);
      onAnsehen(null);
      onFertig();
      await adapter.neuLaden();
    } catch (e) {
      if (istKonflikt(e)) {
        toast.warning(KONFLIKT_TEXT);
        setFragen(false);
        await adapter.neuLaden();
      } else toast.error(fehlerText(e));
    } finally {
      setLaeuft(false);
    }
  }
  const brevo = brevoSatz(mail);

  return (
    <div className="za-version-details">
      {(!aktuell || darfWiederherstellen) && (
        <div className="za-version-knoepfe">
          {!aktuell && (
            gezeigt ? (
              <button type="button" className="btn btn-sm" onClick={() => onAnsehen(null)}>
                <Eye size={14} aria-hidden="true" /> Aktuelle Fassung zeigen
              </button>
            ) : (
              <button type="button" className="btn btn-sm" disabled={!diese.data} onClick={() => diese.data && onAnsehen(diese.data)}>
                <Eye size={14} aria-hidden="true" /> Ansehen
              </button>
            )
          )}
          {darfWiederherstellen && (
            <button type="button" className="btn btn-sm" onClick={() => setFragen(true)}>
              <RotateCcw size={14} aria-hidden="true" /> Diese Fassung wiederherstellen
            </button>
          )}
        </div>
      )}
      {gleichWieJetzt && <p className="za-version-hinweis">Gleicher Inhalt wie die aktuelle Fassung.</p>}

      {vorgaenger && !aktuell && (
        <div className="seg-control seg-control--sm za-version-vergleich" role="group" aria-label="Vergleichen mit">
          <button type="button" className={`seg-btn${vergleich === 'vorher' ? ' active' : ''}`} aria-pressed={vergleich === 'vorher'} onClick={() => setWahl('vorher')}>
            Was sich geändert hat
          </button>
          <button type="button" className={`seg-btn${vergleich === 'jetzt' ? ' active' : ''}`} aria-pressed={vergleich === 'jetzt'} onClick={() => setWahl('jetzt')}>
            Unterschied zu jetzt
          </button>
        </div>
      )}

      {!vorgaenger && aktuell ? (
        <p className="za-version-hinweis">Das ist die erste Version — noch nichts geändert.</p>
      ) : (
        <>
          <p className="za-vergleich-titel">
            {vergleich === 'vorher'
              ? `Was sich in Version ${kopf.nr} gegenüber Version ${vorgaenger?.nr ?? '–'} geändert hat`
              : `Unterschied zwischen Version ${kopf.nr} und der aktuellen Fassung`}
          </p>
          {laedt ? (
            <p className="za-laedt-zeile" role="status"><span className="spinner za-spinner-klein" aria-hidden="true" /> Version wird geladen …</p>
          ) : fehler ? (
            <p className="za-aend-warnung" role="alert">Die Version ließ sich nicht laden: {fehlerText(fehler)}</p>
          ) : unterschiede ? (
            <VergleichListe unterschiede={unterschiede} betreffA={betreffA} betreffB={betreffB} />
          ) : null}
        </>
      )}

      <Rueckfrage
        offen={fragen}
        titel={`Version ${kopf.nr} wiederherstellen?`}
        text={(
          <>
            <p>Die Mail bekommt wieder Text und Betreff von Version {kopf.nr} ({datumZeit(kopf.created_at)}).</p>
            <p>Die aktuelle Fassung bleibt im Verlauf — Du kannst jederzeit zurück.</p>
            {brevo && <p>{brevo}</p>}
            {ungespeichert && <p><strong>Deine ungespeicherten Änderungen im Text gehen dabei verloren.</strong></p>}
          </>
        )}
        ja={ungespeichert ? 'Verwerfen und wiederherstellen' : 'Wiederherstellen'}
        gefaehrlich={ungespeichert}
        laeuft={laeuft}
        onJa={() => { void wiederherstellen(); }}
        onNein={() => setFragen(false)}
      />
    </div>
  );
}

function VergleichListe({ unterschiede, betreffA, betreffB }: { unterschiede: BlockUnterschied[]; betreffA: string | null; betreffB: string | null }) {
  const [alle, setAlle] = useState(false);
  const betreffDiff = useMemo(
    () => (betreffA !== null && betreffB !== null && betreffA !== betreffB ? wortDiff(betreffA, betreffB) : null),
    [betreffA, betreffB],
  );
  if (!unterschiede.length && !betreffDiff) {
    return <p className="za-version-hinweis">Keine Textänderungen — vermutlich nur Gestaltung, Bilder oder Links.</p>;
  }
  const sichtbar = alle ? unterschiede : unterschiede.slice(0, SICHTBAR);
  const rest = unterschiede.length - sichtbar.length;
  return (
    <>
      <ol className="za-vergleich">
        {betreffDiff && (
          <li className="za-vergleich-eintrag za-vergleich-eintrag--geaendert">
            <span className="za-aend-ort">Betreff</span>
            <p className="za-diff"><DiffText teile={betreffDiff} /></p>
          </li>
        )}
        {sichtbar.map((u, i) => (
          <li key={i} className={`za-vergleich-eintrag za-vergleich-eintrag--${u.typ}`}>
            <span className="za-aend-ort">{u.typ === 'geaendert' ? 'Geändert' : u.typ === 'neu' ? 'Neu' : 'Entfernt'}</span>
            <p className="za-diff">
              {u.typ === 'geaendert' && u.teile ? <DiffText teile={diffKuerzen(u.teile).teile} />
                : u.typ === 'neu' ? <ins className="za-diff-neu">{u.nachher}</ins>
                  : <del className="za-diff-weg">{u.vorher}</del>}
            </p>
          </li>
        ))}
      </ol>
      {rest > 0 && (
        <button type="button" className="za-link-knopf" onClick={() => setAlle(true)}>
          {rest === 1 ? '1 weitere Stelle zeigen' : `${rest} weitere Stellen zeigen`}
        </button>
      )}
    </>
  );
}
